import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

// Import the blog post data and type from Blog.tsx
import { SAMPLE_BLOG_POSTS } from './Blog';
import type { BlogPost } from './Blog';

// This is a minimal placeholder for when no blog post is found
const SAMPLE_BLOG_POST: BlogPost = {
  slug: 'placeholder-post',
  title: 'Sample Blog Post',
  date: new Date().toISOString(),
  author: 'Admin',
  category: 'General',
  excerpt: 'This is a placeholder for when no blog post content is found. Please check the URL or visit our blog homepage to see our latest articles.',
  content: `
# Sample Blog Post

This is a placeholder post. Actual blog content will be loaded dynamically from your CMS.

Please visit the blog homepage to see our latest articles on car financing and loan options.
  `,
  thumbnail: '/images/uploads/placeholder.jpg',
  tags: ['placeholder'],
  seo_description: 'This is a placeholder blog post.',
  seo_keywords: 'placeholder, sample post'
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

// Custom components for ReactMarkdown to apply Tailwind CSS classes
const MarkdownComponents = {
  h1: (props: any) => <h1 className="text-3xl font-bold mt-8 mb-6" {...props} />,
  h2: (props: any) => <h2 className="text-2xl font-bold mt-7 mb-4" {...props} />,
  h3: (props: any) => <h3 className="text-xl font-bold mt-6 mb-3" {...props} />,
  h4: (props: any) => <h4 className="text-lg font-bold mt-5 mb-2" {...props} />,
  h5: (props: any) => <h5 className="text-base font-bold mt-4 mb-2" {...props} />,
  p: (props: any) => <p className="mb-4" {...props} />,
  a: (props: any) => <a className="text-blue-600 hover:underline" {...props} />,
  ul: (props: any) => <ul className="list-disc pl-6 my-4" {...props} />,
  ol: (props: any) => <ol className="list-decimal pl-6 my-4" {...props} />,
  li: (props: any) => <li className="mb-2" {...props} />,
  blockquote: (props: any) => <blockquote className="pl-4 border-l-4 border-blue-500 italic text-gray-600 my-4" {...props} />,
  code: (props: any) => {
    // Inline code
    if (!props.className) {
      return <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props} />;
    }
    // Code block
    return <code className="block bg-gray-100 p-2 rounded text-sm" {...props} />;
  },
  pre: (props: any) => <pre className="bg-gray-100 p-4 rounded overflow-x-auto mb-4" {...props} />,
  img: (props: any) => <img className="max-w-full h-auto my-4" {...props} />,
  hr: () => <hr className="my-6 border-t border-gray-300" />,
  table: (props: any) => <table className="min-w-full border-collapse border border-gray-300 my-4" {...props} />,
  th: (props: any) => <th className="border px-4 py-2 bg-gray-100" {...props} />,
  td: (props: any) => <td className="border px-4 py-2" {...props} />,
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // Data is baked in at build time (see src/pages/Blog.tsx), so the post
  // resolves synchronously during render — this is what lets the page prerender
  // with its real article and per-route <head> instead of an empty shell.
  const blogPost = SAMPLE_BLOG_POSTS.find((p) => p.slug === slug) || null;

  // If no post is found, show the not found message
  if (!blogPost) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Blog Post Not Found</h1>
        <p className="mb-6">The blog post you're looking for doesn't exist or has been moved.</p>
        <Link to="/blog/" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft size={16} className="mr-1" /> Back to Blog
        </Link>
      </div>
    );
  }
  
  // Use the found post, supplementing with default content if needed
  const post = {
    ...blogPost,
    // If the found post doesn't have content, keep the default content
    content: blogPost.content || SAMPLE_BLOG_POST.content
  };

  // The page already renders the post title as the page <h1> below, and every
  // post's markdown body opens with a `# Title` heading. Strip that leading H1
  // so each post has exactly one <h1> (avoids a duplicate-H1 SEO smell).
  const bodyContent = post.content.replace(/^﻿?\s*#\s+.+(\r?\n)+/, '');

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <SEOHead
        title={post.title + " | Car Loan Calculator Blog"}
        description={post.seo_description || post.excerpt}
        keywords={post.seo_keywords || post.tags.join(', ')}
      />
      
      <Link to="/blog/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6">
        <ArrowLeft size={16} className="mr-1" /> Back to Blog
      </Link>
      
      <article className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="h-64 md:h-80 overflow-hidden">
          <img 
            src={post.thumbnail} 
            alt={post.title} 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="p-6 md:p-8">
          <div className="mb-6">
            <span className="inline-block bg-blue-600 text-white px-3 py-1 rounded text-sm mb-4">
              {post.category}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center text-sm text-gray-500 mb-4 space-x-4">
              <div className="flex items-center">
                <Calendar size={16} className="mr-1" />
                <span>{formatDate(post.date)}</span>
              </div>
              <div className="flex items-center">
                <User size={16} className="mr-1" />
                <span>{post.author}</span>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.map((tag: string) => (
                <span key={tag} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                  <Tag size={12} className="inline mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          <div className="prose prose-blue max-w-none">
            <ReactMarkdown 
              components={MarkdownComponents}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {bodyContent}
            </ReactMarkdown>
          </div>
          
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Ready to calculate your car loan?</h3>
            <Link 
              to="/car-loan-calculator/" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Use Our Car Loan Calculator
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost; 